// SheetViewer.js
//
// One sheet copy rendered read-only in the shared FortuneSheet viewer, with a sheet plan's
// tables painted over it: each table outlined in a colour of its own, its header band
// tinted, an ignored table dashed, and, when one table (or the sheet's header fields) is
// focused, everything outside it washed out. The file review dialog's file pane and the
// sheet workspace both draw a sheet through this.
//
// The overlay never touches the workbook's cells. FortuneSheet calls a hook for every
// visible cell with the cell's rectangle on the canvas, and the hook paints over the
// cell's own Excel formatting, so dropping the overlay restores the sheet exactly.
//
// The sheet copy has hidden rows and columns physically removed, so its grid coordinates
// ARE the plan's coordinates: row 1 is row 1, column A is column A, with no translation.

import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import ViewerErrorBoundary from '../../common/ViewerErrorBoundary';
import { fetchGcsBlobByPrefix } from '../../../services/api_utils';
import { tableScope } from '../../../utils/blobPaths';
import { columnLetterToIndex } from '../../../utils/sheetPlans';

const SpreadsheetViewer = lazy(() => import('../../common/SpreadsheetViewer'));

// The storage family of a file's sheet copies, as functions/common/blob_paths.py names it.
const SHEET_COPIES = 'sheet_copies';

// Region colours. Four hues, cycled: the outline and swatch colour of each table.
export const REGION_PALETTE = ['#2563eb', '#059669', '#9333ea', '#ea580c'];

// The grey-out wash, painted OVER the cell's own formatting, so an area outside the
// focused table recedes without the workbook's data ever being touched.
const WASH_FILL = 'rgba(246, 247, 249, 0.84)';
const OUTLINE_WIDTH = 2;
const FOCUSED_OUTLINE_WIDTH = 3;

// The colour of the table at `index` in its plan, the same in the overlay and in every
// list that names the table.
export function regionColour(index) {
  return REGION_PALETTE[index % REGION_PALETTE.length];
}

export function withAlpha(hex, alpha) {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

// ── The overlay model ───────────────────────────────────────────────────────────────
// Every rectangle is in 0-based inclusive FortuneSheet coordinates, with Infinity meaning
// "to the sheet's edge", so the four bands outside a focused table need no sheet extent.

function regionRect(entry) {
  return {
    r0: entry.region.header_first_row - 1,
    r1: entry.region.body_last_row - 1,
    c0: entry.colFirst - 1,
    c1: entry.colLast - 1,
  };
}

function inRect(rect, row, column) {
  return row >= rect.r0 && row <= rect.r1 && column >= rect.c0 && column <= rect.c1;
}

// What to paint for these tables. `focus` is a region id, 'fields' (the sheet's header
// fields: everything that is not a table stays lit), or null (every table outlined).
function buildMask(entries, focus) {
  const mask = { washes: [], outlines: [], headerWash: null, divider: null, rail: null };
  const drawable = entries.filter((entry) => entry.drawable);

  if (focus === 'fields') {
    drawable.forEach((entry) => mask.washes.push(regionRect(entry)));
    return mask;
  }

  const focused = drawable.find((entry) => entry.key === focus) || null;
  if (!focused) {
    drawable.forEach((entry) => mask.outlines.push({
      ...regionRect(entry),
      colour: regionColour(entry.index),
      width: OUTLINE_WIDTH,
      dashed: entry.ignored,
    }));
    return mask;
  }

  const rect = regionRect(focused);
  const colour = regionColour(focused.index);
  mask.washes.push({ r0: 0, r1: rect.r0 - 1, c0: 0, c1: Infinity });
  mask.washes.push({ r0: rect.r1 + 1, r1: Infinity, c0: 0, c1: Infinity });
  mask.washes.push({ r0: rect.r0, r1: rect.r1, c0: 0, c1: rect.c0 - 1 });
  mask.washes.push({ r0: rect.r0, r1: rect.r1, c0: rect.c1 + 1, c1: Infinity });

  // Rows inside the rectangle that are not ingested: the rows left out, and any gap
  // between the header band and the body.
  const region = focused.region;
  const skipped = new Set(region.exclude_rows || []);
  for (let row = region.header_last_row + 1; row < region.body_first_row; row += 1) skipped.add(row);
  skipped.forEach((row) => mask.washes.push({
    r0: row - 1, r1: row - 1, c0: rect.c0, c1: rect.c1,
  }));

  mask.headerWash = {
    r0: region.header_first_row - 1,
    r1: region.header_last_row - 1,
    c0: rect.c0,
    c1: rect.c1,
    colour,
  };
  mask.divider = { row: region.header_last_row - 1, c0: rect.c0, c1: rect.c1, colour };
  // The focused table is drawn solid even when it is ignored: it is what is being looked at.
  mask.outlines.push({ ...rect, colour, width: FOCUSED_OUTLINE_WIDTH, dashed: false });
  mask.rail = { r0: rect.r0, r1: rect.r1, colour };
  return mask;
}

// The canvas hooks. Lines are inset by half their width so the next cell's own fill
// cannot clip them.
function buildMaskHooks(mask) {
  const paintOutline = (renderCtx, outline, row, column, startX, startY, endX, endY) => {
    if (!inRect(outline, row, column)) return;
    const inset = outline.width / 2;
    renderCtx.strokeStyle = outline.colour;
    renderCtx.lineWidth = outline.width;
    renderCtx.globalAlpha = outline.dashed ? 0.55 : 1;
    renderCtx.setLineDash(outline.dashed ? [6, 4] : []);
    renderCtx.beginPath();
    if (row === outline.r0) {
      renderCtx.moveTo(startX, startY + inset);
      renderCtx.lineTo(endX, startY + inset);
    }
    if (row === outline.r1) {
      renderCtx.moveTo(startX, endY - inset);
      renderCtx.lineTo(endX, endY - inset);
    }
    if (column === outline.c0) {
      renderCtx.moveTo(startX + inset, startY);
      renderCtx.lineTo(startX + inset, endY);
    }
    if (column === outline.c1) {
      renderCtx.moveTo(endX - inset, startY);
      renderCtx.lineTo(endX - inset, endY);
    }
    renderCtx.stroke();
    renderCtx.setLineDash([]);
    renderCtx.globalAlpha = 1;
  };

  return {
    afterRenderCell: (cell, info, renderCtx) => {
      const { row, column, startX, startY, endX, endY } = info;
      renderCtx.save();
      if (mask.washes.some((rect) => inRect(rect, row, column))) {
        renderCtx.fillStyle = WASH_FILL;
        renderCtx.fillRect(startX, startY, endX - startX, endY - startY);
      } else if (mask.headerWash && inRect(mask.headerWash, row, column)) {
        renderCtx.fillStyle = withAlpha(mask.headerWash.colour, 0.12);
        renderCtx.fillRect(startX, startY, endX - startX, endY - startY);
      }
      if (mask.divider
        && row === mask.divider.row
        && column >= mask.divider.c0
        && column <= mask.divider.c1) {
        renderCtx.strokeStyle = mask.divider.colour;
        renderCtx.lineWidth = 2;
        renderCtx.beginPath();
        renderCtx.moveTo(startX, endY - 1);
        renderCtx.lineTo(endX, endY - 1);
        renderCtx.stroke();
      }
      mask.outlines.forEach((outline) => paintOutline(
        renderCtx, outline, row, column, startX, startY, endX, endY,
      ));
      renderCtx.restore();
    },
    // The row rail: which rows belong to the focused table stays visible when the sheet
    // is scrolled far to the right, where the table's own columns are off screen.
    afterRenderRowHeaderCell: (rowNumber, rowIndex, top, width, height, renderCtx) => {
      if (!mask.rail || rowIndex < mask.rail.r0 || rowIndex > mask.rail.r1) return;
      renderCtx.save();
      renderCtx.fillStyle = withAlpha(mask.rail.colour, 0.18);
      renderCtx.fillRect(0, top, width, height);
      renderCtx.restore();
    },
  };
}

// The sheet's extent, from the converted workbook. lastRow spans every addressed cell
// (the bound a table's rows may not pass), while firstRow counts only cells that carry a
// value: it is where a new table starts.
function sheetExtent(sheets) {
  const cells = sheets?.[0]?.celldata || [];
  let firstRow = null;
  let lastRow = 0;
  let lastCol = 0;
  cells.forEach(({ r, c, v }) => {
    if (r + 1 > lastRow) lastRow = r + 1;
    if (c + 1 > lastCol) lastCol = c + 1;
    const value = v?.v;
    if (value === null || value === undefined || value === '') return;
    if (firstRow === null || r + 1 < firstRow) firstRow = r + 1;
  });
  return { firstRow: firstRow || 1, lastRow, lastCol: Math.max(lastCol, 1) };
}

// Props:
//   companyId, tableId, fileId   whose sheet copy this is: the path is read only inside the
//                                file's own sheet_copies folder
//   sheetCopyPath, sheetName     file.assets.sheet_copies[sheetName], and that sheet's name
//   regions                      the plan's tables, in plan order (the order sets the colours)
//   ignoredRegionIds             the ids of the tables to draw dashed
//   focus                        a region id, 'fields', or null
//   onSelectionChange            optional: a range dragged on the sheet, as 1-based
//                                { rowFirst, rowLast, colFirst, colLast }
//   onSheetLoad                  optional: the sheet's extent { firstRow, lastRow, lastCol }
export default function SheetViewer({
  companyId,
  tableId,
  fileId,
  sheetCopyPath,
  sheetName,
  regions = [],
  ignoredRegionIds = [],
  focus = null,
  onSelectionChange = null,
  onSheetLoad = null,
}) {
  const [sheetBytes, setSheetBytes] = useState(null);
  const [loadState, setLoadState] = useState('loading');   // 'loading' | 'ready' | 'failed'
  const [loadAttempt, setLoadAttempt] = useState(0);

  // The callbacks are read through refs so that the hooks handed to FortuneSheet keep one
  // identity across renders: a new hooks object repaints the whole grid.
  const selectionHandlerRef = useRef(onSelectionChange);
  selectionHandlerRef.current = onSelectionChange;
  const loadHandlerRef = useRef(onSheetLoad);
  loadHandlerRef.current = onSheetLoad;

  // A pointer read off the file document is followed only inside that file's own
  // sheet copies, so a document naming another file's or another tenant's blob shows
  // nothing.
  const sheetCopyFolder = `${tableScope(companyId, tableId)}/${SHEET_COPIES}/${fileId}/`;
  const pathIsOwned = typeof sheetCopyPath === 'string'
    && sheetCopyPath.startsWith(sheetCopyFolder)
    && !sheetCopyPath.split('/').includes('..');

  useEffect(() => {
    if (!pathIsOwned) return undefined;
    let cancelled = false;
    setSheetBytes(null);
    setLoadState('loading');
    fetchGcsBlobByPrefix(sheetCopyPath)
      .then((blob) => blob.arrayBuffer())
      .then((bytes) => {
        if (cancelled) return;
        setSheetBytes(bytes);
        setLoadState('ready');
      })
      .catch((error) => {
        console.error('Failed to load a sheet copy:', error);
        if (!cancelled) setLoadState('failed');
      });
    return () => { cancelled = true; };
  }, [sheetCopyPath, pathIsOwned, loadAttempt]);

  // The ignored ids as one text key, so a caller passing a fresh array on every render
  // does not repaint the grid.
  const ignoredKey = [...ignoredRegionIds].sort().join('\u001f');
  const entries = useMemo(() => {
    const ignored = new Set(ignoredKey ? ignoredKey.split('\u001f') : []);
    return (regions || []).map((region, index) => {
      const colFirst = columnLetterToIndex(region.col_first);
      const colLast = columnLetterToIndex(region.col_last);
      const rowsAreWhole = [region.header_first_row, region.body_last_row]
        .every((row) => Number.isInteger(row) && row >= 1);
      return {
        key: region.region_id,
        index,
        region,
        colFirst,
        colLast,
        ignored: ignored.has(region.region_id),
        drawable: rowsAreWhole && colFirst !== null && colLast !== null,
      };
    });
  }, [regions, ignoredKey]);

  const mask = useMemo(() => buildMask(entries, focus), [entries, focus]);

  const reportSelection = useCallback((sheetId, selection) => {
    if (!selectionHandlerRef.current || !selection?.row || !selection?.column) return;
    selectionHandlerRef.current({
      rowFirst: selection.row[0] + 1,
      rowLast: selection.row[1] + 1,
      colFirst: selection.column[0] + 1,
      colLast: selection.column[1] + 1,
    });
  }, []);

  const hooks = useMemo(
    () => ({ ...buildMaskHooks(mask), afterSelectionChange: reportSelection }),
    [mask, reportSelection],
  );

  const handleLoad = useCallback((sheets) => {
    if (loadHandlerRef.current) loadHandlerRef.current(sheetExtent(sheets));
  }, []);

  if (!sheetCopyPath) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
        There is no copy of this sheet to show.
      </Typography>
    );
  }

  if (!pathIsOwned) {
    return <Alert severity="error" sx={{ m: 1 }}>This sheet is not available to show.</Alert>;
  }

  if (loadState === 'failed') {
    return (
      <Alert
        severity="error"
        sx={{ m: 1 }}
        action={<Button color="inherit" size="small" onClick={() => setLoadAttempt((n) => n + 1)}>Retry</Button>}
      >
        This sheet could not be loaded.
      </Alert>
    );
  }

  if (loadState === 'loading' || !sheetBytes) {
    return (
      <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
    );
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, height: '100%' }}>
      <ViewerErrorBoundary key={`${fileId}:${sheetName}`}>
        <Suspense fallback={(
          <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
        )}
        >
          <SpreadsheetViewer
            fileBytes={sheetBytes}
            sheetName={sheetName}
            hooks={hooks}
            onLoad={handleLoad}
            onError={() => {
              setSheetBytes(null);
              setLoadState('failed');
            }}
            showFormulaBar={false}
            showSheetTabs={false}
          />
        </Suspense>
      </ViewerErrorBoundary>
    </Box>
  );
}
